import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  PencilSimple,
  ArrowsIn,
  ArrowsOut,
  Wrench,
  MathOperations,
  Lightbulb,
  BookOpen,
  DotsThree,
  ChatDots,
  Info,
  TreeStructure,
  Checks,
  Graph,
} from "@phosphor-icons/react";
import { useEditorStore } from "../../stores/editor";
import { useUIStore } from "../../stores/ui";
import { useAIStore } from "../../stores/ai";
import { useEquationPlaygroundStore } from "../../stores/equationPlayground";
import { useCollaborationStore } from "../../stores/collaboration";
import { cn } from "../../lib/cn";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";

interface ToolbarAction {
  id: string;
  label: string;
  icon: PhosphorIcon;
}

const PRIMARY_ACTIONS: ToolbarAction[] = [
  { id: "rewrite", label: "Rewrite", icon: PencilSimple },
  { id: "simplify", label: "Simplify", icon: ArrowsIn },
  { id: "expand", label: "Expand", icon: ArrowsOut },
  { id: "fix", label: "Fix", icon: Wrench },
];

const OVERFLOW_ACTIONS: ToolbarAction[] = [
  { id: "proofread", label: "Proofread", icon: Checks },
  { id: "solve", label: "Solve", icon: MathOperations },
  { id: "prove", label: "Prove", icon: TreeStructure },
  { id: "hint", label: "Hint", icon: Lightbulb },
  { id: "cite", label: "Cite", icon: BookOpen },
  { id: "explain", label: "Explain", icon: Info },
  { id: "comment", label: "Comment", icon: ChatDots },
  { id: "playground", label: "Playground", icon: Graph },
];

interface SelectionToolbarProps {
  onAction?: (actionId: string, selectedText: string) => void;
  className?: string;
}

const spring = { type: "spring" as const, stiffness: 500, damping: 30, mass: 0.5 };

const preventEditorFocus = (e: React.MouseEvent | React.PointerEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

export function SelectionToolbar({ onAction, className }: SelectionToolbarProps) {
  const showToolbar = useEditorStore((s) => s.showSelectionToolbar);
  const position = useEditorStore((s) => s.selectionToolbarPosition);
  const selection = useEditorStore((s) => s.selection);
  const toolbarAnchor = useUIStore((s) => s.selectionToolbarAnchor);
  const isProcessing = useAIStore((s) => s.isProcessing);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const selectionRef = useRef(selection);
  useEffect(() => {
    if (selection) selectionRef.current = selection;
  }, [selection]);

  const openPlayground = useEquationPlaygroundStore((s) => s.open);
  const editorView = useEditorStore((s) => s.editorView);
  const openCommentPopover = useCollaborationStore((s) => s.openCommentPopover);
  const setShowToolbar = useEditorStore((s) => s.setShowSelectionToolbar);

  const handleAction = (actionId: string) => {
    if (isProcessing) return;
    const sel = selectionRef.current;
    if (!sel?.text) return;

    if (actionId === "playground") {
      openPlayground(sel.text);
      setOverflowOpen(false);
      return;
    }

    if (actionId === "comment") {
      if (!editorView || !position) return;
      const lineStart = editorView.state.doc.lineAt(sel.from).number;
      const lineEnd = editorView.state.doc.lineAt(sel.to).number;
      openCommentPopover({ position: { x: position.x, y: position.y }, lineStart, lineEnd });
      setShowToolbar(false);
      setOverflowOpen(false);
      return;
    }

    if (onAction) {
      onAction(actionId, sel.text);
    }
    setOverflowOpen(false);
  };

  if (!showToolbar || !position) return null;

  const viewW = typeof window !== "undefined" ? window.innerWidth : 800;
  const toolbarW = 260;
  const toolbarH = 36;

  const clampedX = Math.max(toolbarW / 2 + 8, Math.min(position.x, viewW - toolbarW / 2 - 8));

  const preferBelow = toolbarAnchor === "end";
  const placeAbove = preferBelow
    ? position.y < 0
    : position.y > toolbarH + 8;
  const clampedY = placeAbove
    ? position.y - 8
    : position.y;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: placeAbove ? 4 : -4, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: placeAbove ? 4 : -4, scale: 0.96 }}
        transition={spring}
        className={cn(
          "absolute z-50 flex items-center gap-0.5 px-1 py-0.5",
          "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
          "rounded-[var(--radius-lg)]",
          "backdrop-blur-[var(--blur-sm)]",
          isProcessing ? "shadow-[var(--glow-ai)] border-[var(--color-lever-orange)]/20" : "shadow-[var(--shadow-lg)]",
          className,
        )}
        style={{
          left: clampedX,
          top: clampedY,
          transform: placeAbove ? "translate(-50%, -100%)" : "translate(-50%, 0)",
        }}
        onMouseDown={preventEditorFocus}
        onPointerDown={preventEditorFocus}
      >
        {PRIMARY_ACTIONS.map((action) => {
          const Icon = action.icon;
          return (
            <motion.button
              key={action.id}
              onMouseDown={preventEditorFocus}
              onPointerDown={preventEditorFocus}
              onClick={() => handleAction(action.id)}
              whileHover={{ scale: 1.05, backgroundColor: "var(--bg-surface-hover)" }}
              whileTap={{ scale: 0.95 }}
              transition={spring}
              className={cn(
                "flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-1 text-[11px] font-medium",
                "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                "transition-colors duration-75",
              )}
              title={action.label}
            >
              <Icon size={13} weight="bold" />
              <span>{action.label}</span>
            </motion.button>
          );
        })}

        <div className="mx-0.5 h-4 w-px bg-[var(--border-hairline)]" />

        {/* Overflow menu */}
        <div className="relative">
          <motion.button
            onMouseDown={preventEditorFocus}
            onPointerDown={preventEditorFocus}
            onClick={() => setOverflowOpen(!overflowOpen)}
            whileHover={{ scale: 1.05, backgroundColor: "var(--bg-surface-hover)" }}
            whileTap={{ scale: 0.95 }}
            transition={spring}
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
              "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
              "transition-colors duration-75",
            )}
            title="More actions"
          >
            <DotsThree size={15} weight="bold" />
          </motion.button>

          <AnimatePresence>
            {overflowOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: -4 }}
                transition={spring}
                onMouseDown={preventEditorFocus}
                onPointerDown={preventEditorFocus}
                className={cn(
                  "absolute right-0 z-50 w-36 p-1 mt-1",
                  placeAbove ? "bottom-full mb-1" : "top-full mt-1",
                  "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
                  "rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)]",
                  "backdrop-blur-[var(--blur-sm)]",
                )}
              >
                {OVERFLOW_ACTIONS.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.id}
                      onMouseDown={preventEditorFocus}
                      onPointerDown={preventEditorFocus}
                      onClick={() => handleAction(action.id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-[11px] font-medium text-left",
                        "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
                        "transition-colors duration-75",
                      )}
                    >
                      <Icon size={12} weight="bold" />
                      {action.label}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
